import { useState } from 'react';
import { css } from '@emotion/react';
import { SuperTextarea, SuperTextareaProps } from '@frontend/super-textarea';
import {
  ShortenedUrlHighlightOverlay,
  useBlockedShortenedDomains,
  useShortenedUrlDetection,
} from '@frontend/thread-sending-area';
import { useDebouncedValue } from '@frontend/timer';
import { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { useFileUpload } from '@frontend/design-system';
import { DropzoneMessage } from '../../../common/dropzone-message';
import { ThreadSendingMediaItem } from '../../types';
import { MediaSendingPreviewArea } from './media-sending-preview-area';

type ThreadSendingAreaProps = CommonHTMLAttributes & {
  bodyValue: string;
  setBodyValue: (value: string) => void;
  threadMedia: ThreadSendingMediaItem[];
  disabled?: boolean;
  removeMediaItem: (id: string) => void;
  fileUploadProps: ReturnType<typeof useFileUpload>;
  allowedFileTypes?: string[];
  onSubmit: () => void;
  hideSubmitButton?: boolean;
  extraActions?: SuperTextareaProps['extraActions'];
};

export const ThreadSendingArea = ({
  bodyValue,
  setBodyValue,
  threadMedia,
  removeMediaItem,
  disabled,
  fileUploadProps,
  allowedFileTypes,
  onSubmit,
  hideSubmitButton,
  extraActions,
  ...rest
}: ThreadSendingAreaProps) => {
  const { blockedDomains } = useBlockedShortenedDomains();
  const [textareaScrollTop, setTextareaScrollTop] = useState(0);
  const { debouncedValue: debouncedBodyValue } = useDebouncedValue(bodyValue, 500);
  const { hasShortenedUrl, offendingUrl, offendingUrlIndex } = useShortenedUrlDetection(
    debouncedBodyValue,
    blockedDomains
  );

  const shortenedUrlOverlayStyles = css({
    position: 'absolute',
    top: 'var(--fabric-number-n-4-5)',
    left: 'var(--fabric-number-n-4-5)',
    right: 'var(--fabric-number-n-4-5)',
    bottom: 0,
    pointerEvents: 'none',
  });

  const dropzoneMessageStyles = css({
    position: 'absolute',
    margin: theme.spacing(1),
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.colors.white,
  });

  return (
    <>
      <MediaSendingPreviewArea media={threadMedia} onRemoveMediaItem={(id) => removeMediaItem(id)} />
      <div {...rest}>
        <input type='file' {...fileUploadProps.inputProps} css={{ display: 'none' }} multiple />
        <SuperTextarea
          value={bodyValue}
          onChange={setBodyValue}
          disabled={disabled}
          hasSubmit={!hideSubmitButton}
          onSubmit={onSubmit}
          onBodyScroll={(e) => setTextareaScrollTop(e.currentTarget.scrollTop)}
          onImageClick={() => fileUploadProps.handleOnClick()}
          extraActions={extraActions}
        >
          {hasShortenedUrl && offendingUrl !== null && offendingUrlIndex !== null && (
            // The SuperTextarea <form> has position:relative, padding-top:8px.
            // The textAreaContainer inside has padding:8px 16px 0 16px.
            // So textarea text starts at top:16px, left:16px from the form edge.
            <div css={shortenedUrlOverlayStyles}>
              <ShortenedUrlHighlightOverlay
                text={bodyValue}
                offendingUrl={offendingUrl}
                offendingUrlIndex={offendingUrlIndex}
                scrollTop={textareaScrollTop}
                overlayBaseStyles={{ lineHeight: 1.375 }}
              />
            </div>
          )}
          {fileUploadProps.isDragging && (
            <DropzoneMessage allowedFileTypes={allowedFileTypes} css={dropzoneMessageStyles} />
          )}
        </SuperTextarea>
      </div>
    </>
  );
};
